<template>
	<div class="hotWrap">
		<div v-if="sitePending" class="site-loading">
			<van-loading type="spinner" color="#c0c4cc" />
		</div>
		<template v-else>
			<div v-if="isLogin && firstSiteList.length > 0" class="first-operated-site-box">
				<div class="site-title-box">
					<div class="site-title">
						<div class="underline"></div>
						<span>已首营站点</span>
					</div>
					<button class="view-all" type="button" @click="handleViewAll">
						<span>查看全部</span>
						<i class="iconfont icon-maijia_youjin" style="font-size: 0.87rem"></i>
					</button>
				</div>
				<div class="first-operated-site-list">
					<div v-for="item in firstSiteList.slice(0, 4)" :key="item.companyId" class="first-operated-site-item">
						<a :href="getSiteHref(item)" class="site-info-box" @click="handleGxzzSiteEntry(item)">
							<div class="site-name">{{ getSiteName(item) }}</div>
							<div class="site-product">
								<span class="site-product-text">已上架商品</span>
								<span class="site-product-text site-product-count">{{ item.productNum }}</span>
							</div>
						</a>
					</div>
				</div>
			</div>

			<div v-if="isLogin && commonlySiteList.length > 0" class="commonly-used-site-box">
				<div class="site-title">
					<div class="underline"></div>
					<span>常用站点</span>
				</div>
				<div class="commonly-used-site-list">
					<div class="site-items-wrapper">
						<a v-for="item in commonlySiteList" :key="item.companyId" :href="getSiteHref(item)" class="commonly-used-site-item" @click="handleGxzzSiteEntry(item)">
							<span class="commonly-used-site-item-text">{{ getSiteName(item) }}</span>
						</a>
					</div>
				</div>
			</div>

			<section ref="allSiteBoxRef" class="all-site-box">
				<div ref="filterBarRef" class="filter-bar">
					<div class="filter-text">只查看有商品站点</div>
					<ClientOnly>
						<van-switch v-model="checked" size="16px" active-color="#455bd9" />
					</ClientOnly>
				</div>

				<indexBar :options="indexedOptions" :height="indexBarHeight" @click="handleGxzzSiteEntry">
					<template #item="{ item }">
						<div class="index-bar__content">
							<div class="index-bar__content-text">{{ item.websiteName }}</div>
							<div class="index-bar__content-num">
								<span class="index-bar__content-label">已上架商品</span>
								<span>{{ item.productNum }}</span>
							</div>
						</div>
					</template>
				</indexBar>
			</section>
		</template>

		<ClientOnly>
			<FooterBar :checks="2" />
		</ClientOnly>
	</div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { queryUserAllCompany, queryUserFirstSellSites, queryUserTopSellerCompany } from '@/api/hotSite';
import indexBar from '@/components/indexBar/indexBar.vue';
import { GXZZ_TRACKING_CHANNEL, GXZZ_TRACKING_TARGET, setGxzzTrackingContext } from '@/utils/gxzzTracking';
const checked = ref(false);
const indexBarHeight = ref('60vh');
const allSiteBoxRef = ref(null);
const filterBarRef = ref(null);
const accessToken = useCookie('cmdc_access_token_mobile');
const isLogin = computed(() => Boolean(accessToken.value));

/**
 * 统一生成子站首页链接，保证源码里直接输出真实 href，便于抓取站点入口。
 */
const getSiteHref = item => {
	if (!item?.companyId) return '/company';
	return `/${item.companyId}`;
};

const getSiteName = item => {
	return (item?.websiteName || item?.companyName || '').trim();
};

/**
 * 记录从总站站点导航进入国械智造子站的渠道，不改变子站短链接。
 * @param {Object} item 站点数据
 */
const handleGxzzSiteEntry = item => {
	if (Number(item?.isGxzz) !== 1 || !item?.companyId) return;
	setGxzzTrackingContext(GXZZ_TRACKING_TARGET.SUBSITE, item.companyId, GXZZ_TRACKING_CHANNEL.TOP_SITE_NAV);
};

const normalizeSiteList = response => {
	if (response?.code == 200 && Array.isArray(response.data)) {
		return response.data;
	}
	return [];
};

const normalizeAllSiteList = response => {
	if (response?.code == 200 && response.data && typeof response.data === 'object') {
		return response.data;
	}
	return {};
};

const asyncDataKey = `company-page-data-${isLogin.value ? 'login' : 'guest'}`;

const { data: sitePageData, pending: sitePending } = await useAsyncData(
	asyncDataKey,
	async () => {
		const [allSiteResponse, firstSiteResponse, commonSiteResponse] = await Promise.all([
			queryUserAllCompany({ productNum: 0 }),
			isLogin.value ? queryUserFirstSellSites() : Promise.resolve(null),
			isLogin.value ? queryUserTopSellerCompany() : Promise.resolve(null),
		]);

		return {
			allSiteList: normalizeAllSiteList(allSiteResponse),
			firstSiteList: normalizeSiteList(firstSiteResponse),
			commonlySiteList: normalizeSiteList(commonSiteResponse),
		};
	},
	{ lazy: true }
);

const firstSiteList = computed(() => sitePageData.value?.firstSiteList || []);
const commonlySiteList = computed(() => sitePageData.value?.commonlySiteList || []);
const allSiteList = computed(() => sitePageData.value?.allSiteList || {});

const indexedOptions = computed(() => {
	let filteredData = {};

	if (checked.value) {
		for (const letter in allSiteList.value) {
			if (!Object.prototype.hasOwnProperty.call(allSiteList.value, letter)) continue;
			const sites = (allSiteList.value[letter] || []).filter(site => Number(site.productNum) > 0);
			if (sites.length > 0) {
				filteredData[letter] = sites;
			}
		}
	} else {
		filteredData = { ...allSiteList.value };
	}

	const uniqueGroups = {};

	for (const letter in filteredData) {
		const sites = filteredData[letter] || [];
		const uniqueMap = new Map();

		sites.forEach(site => {
			if (!site?.companyId) return;
			const name = getSiteName(site);
			if (!name) return;

			uniqueMap.set(site.companyId, {
				id: site.companyId,
				websiteName: name,
				productNum: site.productNum,
				linkUrl: site.linkUrl,
				companyId: site.companyId,
				isGxzz: site.isGxzz,
				href: getSiteHref(site),
			});
		});

		if (uniqueMap.size > 0) {
			uniqueGroups[letter] = Array.from(uniqueMap.values()).sort((a, b) => a.websiteName.localeCompare(b.websiteName, 'zh-CN'));
		}
	}

	const letters = Object.keys(uniqueGroups).sort((a, b) => {
		if (a === '#') return 1;
		if (b === '#') return -1;
		return a.localeCompare(b, 'en');
	});

	return letters.map(letter => ({
		name: letter,
		children: uniqueGroups[letter],
	}));
});

const calculateIndexBarHeight = () => {
	if (typeof window === 'undefined') return;
	const viewportHeight = window.innerHeight || 667;
	const sectionTop = allSiteBoxRef.value?.getBoundingClientRect?.().top || 0;
	const footerHeight = document.querySelector('.tabBar')?.getBoundingClientRect?.().height || 50;
	const filterHeight = filterBarRef.value?.getBoundingClientRect?.().height || 0;
	const reserveBottom = 8;
	const extraHeight = 20;
	const availableHeight = Math.max(viewportHeight - sectionTop - footerHeight - reserveBottom - filterHeight + extraHeight, 200);
	indexBarHeight.value = `${Math.floor(availableHeight)}px`;
};

const handleViewAll = () => {
	if (typeof window === 'undefined') return;
	const el = allSiteBoxRef.value;
	if (el && typeof el.scrollIntoView === 'function') {
		el.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}
};

watch(
	indexedOptions,
	async () => {
		await nextTick();
		calculateIndexBarHeight();
	},
	{ immediate: true }
);

onMounted(async () => {
	await nextTick();
	calculateIndexBarHeight();
	window.addEventListener('resize', calculateIndexBarHeight);
	// 隐藏最外层页面滚动条，列表滚动仍由 indexBar 内层承担
	document.documentElement.style.overflow = 'hidden';
	document.body.style.overflow = 'hidden';
});

onBeforeUnmount(() => {
	if (typeof window !== 'undefined') {
		window.removeEventListener('resize', calculateIndexBarHeight);
		document.documentElement.style.overflow = '';
		document.body.style.overflow = '';
	}
});
</script>

<style scoped lang="scss">
.site-title {
	font-size: 1.33rem;
	font-weight: bold;
	color: #303133;
	position: relative;

	.underline {
		position: absolute;
		left: 0;
		bottom: 0;
		width: 56px;
		height: 5px;
		background-image: url('/staticSeo/images/underline-bg.png');
		background-size: 100% 100%;
		background-repeat: no-repeat;
	}
}

.site-loading {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 9;
}

.hotWrap {
	width: 100%;
	min-height: 100vh;
	background: #ffffff;
	position: relative;
	padding-bottom: 60px;
	background-image: url('/staticSeo/images/web-site-bg.png');
	background-size: 100% 100%;
	background-position: center;
	background-repeat: no-repeat;
	overflow: hidden;
}

.first-operated-site-box {
	padding: 4px 16px 9px;

	.site-title-box {
		display: flex;
		justify-content: space-between;
		align-items: center;
		height: 44px;
		padding: 10px 0;

		.view-all {
			border: none;
			background: transparent;
			padding: 0;
			font-size: 12px;
			color: #595959;
			display: flex;
			align-items: center;
			gap: 4px;
			cursor: pointer;
		}
	}

	.first-operated-site-list {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;

		.first-operated-site-item {
			width: 167.5px;
			height: 51px;
			border-radius: 3px;
			background: linear-gradient(270deg, #f5faff 5%, #e0f0ff 100%);
			display: flex;
			align-items: center;
			padding: 8px 12px;
			gap: 8px;

			.site-info-box {
				flex: 1;
				display: flex;
				flex-direction: column;
				align-items: flex-start;
				text-align: left;
				text-decoration: none;

				.site-name {
					font-size: 13px;
					font-weight: bold;
					color: #1f1f1f;
					margin-bottom: 2px;
				}

				.site-product {
					font-size: 12px;
					color: #595959;
					display: flex;
					gap: 4px;

					.site-product-count {
						font-weight: bold;
					}
				}
			}
		}
	}
}

.commonly-used-site-box {
	padding: 0 16px;
	margin-top: 6px;
}

.all-site-box {
	padding: 6px 16px 0;
	position: relative;
}

.commonly-used-site-list {
	white-space: nowrap;
	width: 100%;
	margin-top: 10px;
	padding-bottom: 16px;
	overflow-x: auto;

	&::-webkit-scrollbar {
		display: none;
	}

	-ms-overflow-style: none;
	scrollbar-width: none;
}

.site-items-wrapper {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 8px;

	.commonly-used-site-item {
		height: 29px;
		border-radius: 3px;
		padding: 0 8px;
		background: #ecf3ff;
		border: 1px solid #9ab1f6;
		display: inline-flex;
		align-items: center;
		text-decoration: none;

		.commonly-used-site-item-text {
			line-height: 27px;
			font-family: Source Han Sans;
			font-size: 13px;
			font-weight: bold;
			color: #2e40a6;
			white-space: nowrap;
		}
	}
}

.index-bar__content {
	display: flex;
	flex-direction: column;
	gap: 2px;
	padding: 8px 0;

	.index-bar__content-text {
		font-size: 12px;
		font-weight: bold;
		color: #333333;
	}

	.index-bar__content-num {
		font-size: 11px;
		font-weight: bold;
		color: #595959;

		.index-bar__content-label {
			font-weight: normal;
			margin-right: 6px;
		}
	}
}

.filter-bar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	background: transparent;
	position: absolute;
	top: 7px;
	right: 30px;
	z-index: 10;

	.filter-text {
		font-size: 14px;
		color: #595959;
	}
}
</style>
